import React from 'react';
import { Col, DatePicker, Form, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/Auth';
import { ListActionsBar } from '@/components/_components/ListActionsBar';
import { FilterBlock } from '@/components/FilterBlock';
import {
    fetchInvoicesV2,
    useGetInvoicesV2,
} from '@/store/invoice/useGetInvoicesV2';
import { invoicesColumns, withoutShopmanagerColumns } from '../columns';
import {
    ADMIN_STATUS_OPTIONS,
    INVOICE_MONITORED_FIELDS,
    NEW_INVOICE_SUPPORT_URL,
    STATUS_OPTIONS,
} from '../const';
import { exportInvoices } from '../schemaFile';
import { InvoicesFormValues } from '../types';
import { useListTab } from '../useListTab';
import * as S from '../styled';
import { AdminShopmanagerFields } from './AdminShopmanagerFields';
import { ExportPanel } from './ExportPanel';
import { ResultsTable } from './ResultsTable';

type AppliedFilters = {
    'filters[month]'?: string;
    'filters[status]'?: string;
    'filters[shopmanagerId]'?: string;
    'filters[shopmanagerEmail]'?: string;
};

const toAppliedFilters = (values: InvoicesFormValues): AppliedFilters => ({
    'filters[month]': values.month?.startOf('month').format('YYYY-MM-DD'),
    'filters[status]': values.status,
    'filters[shopmanagerId]': values.shopManagerId,
    'filters[shopmanagerEmail]': values.email,
});

const EMPTY_FILTERS: AppliedFilters = {};

export function InvoicesTab() {
    const { isAdmin, data: auth } = useAuth();
    const [form] = Form.useForm<InvoicesFormValues>();
    const {
        filters,
        pagination,
        onSearch,
        onReset,
        onPageChange,
        onExport,
        isExporting,
    } = useListTab({
        form,
        emptyFilters: EMPTY_FILTERS,
        toAppliedFilters,
        fetchPage: (appliedFilters, page, perPage) =>
            fetchInvoicesV2({ ...appliedFilters, page, perPage }),
        exportRows: (rows) => exportInvoices(rows, isAdmin),
        exportErrorMessage: 'Falha ao exportar boletos!',
    });

    const {
        data: invoices,
        isFetching,
        isError,
        refetch,
    } = useGetInvoicesV2(
        { ...filters, ...pagination },
        !!auth.user &&
            (!isAdmin ||
                !!(
                    filters['filters[shopmanagerId]'] ||
                    filters['filters[shopmanagerEmail]']
                )),
    );

    return (
        <S.TabContent>
            <Form
                form={form}
                layout="vertical"
                initialValues={{ showFilter: 'filter' }}
                onFinish={onSearch}
            >
                <ListActionsBar
                    monitoredFields={INVOICE_MONITORED_FIELDS}
                    exportContent={
                        <ExportPanel
                            tabLabel="Boletos"
                            disabled={isFetching || !invoices?.data.length}
                            loading={isExporting}
                            onGenerate={onExport}
                        />
                    }
                >
                    <FilterBlock
                        onReset={onReset}
                        loading={isFetching}
                        justify="start"
                    >
                        {isAdmin && <AdminShopmanagerFields required />}
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item<InvoicesFormValues>
                                label="Mês de vencimento"
                                name="month"
                            >
                                <DatePicker
                                    picker="month"
                                    format="MMMM/YYYY"
                                    placeholder="Selecionar mês"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item<InvoicesFormValues>
                                label="Status"
                                name="status"
                            >
                                <Select
                                    placeholder="Todos"
                                    allowClear
                                    options={
                                        isAdmin
                                            ? ADMIN_STATUS_OPTIONS
                                            : STATUS_OPTIONS.map((option) => ({
                                                  value: option.value,
                                                  label: (
                                                      <S.OptionWithDot>
                                                          <S.StatusDot
                                                              $color={
                                                                  option.dot
                                                              }
                                                          />
                                                          {option.label}
                                                      </S.OptionWithDot>
                                                  ),
                                              }))
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </FilterBlock>
                </ListActionsBar>
            </Form>

            <S.Notice>
                <InfoCircleOutlined />
                <span>
                    Boletos não pagos são cancelados pelo banco 60 dias após o
                    vencimento e passam a aparecer como{' '}
                    <strong>Atrasado</strong>, sem código de barras. O valor
                    continua em aberto: para regularizar,{' '}
                    <a
                        href={NEW_INVOICE_SUPPORT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        fale com o suporte
                    </a>
                    .
                </span>
            </S.Notice>

            <ResultsTable
                rowKey="code"
                columns={
                    isAdmin
                        ? invoicesColumns
                        : withoutShopmanagerColumns(invoicesColumns)
                }
                data={invoices?.data ?? []}
                total={invoices?.meta.total ?? 0}
                loading={isFetching}
                pagination={pagination}
                onPageChange={onPageChange}
                onReset={onReset}
                isError={isError}
                onRetry={refetch}
                emptyText="Nenhum boleto encontrado para os filtros selecionados."
                errorText="Falha ao buscar boletos."
            />
        </S.TabContent>
    );
}
